import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Mail, Instagram, Linkedin } from "lucide-react";

function WhatsappIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
    </svg>
  );
}

const occasionOptions = [
  "Wedding",
  "Anniversary",
  "Milestone Birthday",
  "Baby Shower / Birth",
  "Bridal Shower",
  "Festive / Diwali",
  "Corporate Gifting",
  "Just because",
  "Something else",
];

const socials = [
  {
    id: "instagram",
    href: "https://www.instagram.com/gifting.concierge",
    label: "Gifting Concierge on Instagram",
    Icon: Instagram,
  },
  {
    id: "linkedin",
    href: "https://www.linkedin.com/company/gifting-concierge/",
    label: "Gifting Concierge on LinkedIn",
    Icon: Linkedin,
  },
];

function ContactChannel({ testId, href, external, icon, label, value, valueClass }) {
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      data-testid={testId}
      className="group flex items-center gap-5 bg-[#fdfbf7] hover:bg-[#fff] hover:translate-x-1 transition-all duration-300 px-5 py-4 rounded-full shadow-[0_12px_36px_-22px_rgba(42,29,21,0.4)]"
    >
      {icon}
      <span className="flex flex-wrap items-baseline gap-x-3">
        <span className="overline text-[#5C4334]">{label}</span>
        <span className={valueClass}>{value}</span>
      </span>
    </a>
  );
}

function SocialLinks() {
  return (
    <div className="mt-10">
      <p className="overline text-[#5C4334] mb-4">Follow our work</p>
      <div className="flex items-center gap-4">
        {socials.map(({ id, href, label, Icon }) => (
          <a
            key={id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`contact-${id}`}
            aria-label={label}
            className="w-12 h-12 rounded-full bg-[#fdfbf7] text-[#8B4513] hover:bg-[#8B4513] hover:text-[#FDFBF7] hover:-translate-y-0.5 flex items-center justify-center shadow-[0_12px_36px_-22px_rgba(42,29,21,0.4)] transition-all duration-300"
          >
            <Icon className="w-[22px] h-[22px]" strokeWidth={1.6} />
          </a>
        ))}
      </div>
    </div>
  );
}

function ContactInfo({ waUrl, mailUrl }) {
  return (
    <div className="lg:col-span-5">
      <p className="overline text-[#8B4513] mb-6" data-testid="contact-overline">
        Let's create something
      </p>

      <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight text-[#2A1D15]">
        Start your gift{" "}
        <span className="italic text-[#8B4513]">with us.</span>
      </h2>

      <p className="mt-8 text-base md:text-lg text-[#5C4334] font-light leading-[1.85] max-w-md">
        Tell us the occasion and the person you're celebrating — we'll take
        it from there, and turn it into a memory worth keeping.
      </p>

      <div className="mt-12 space-y-4">
        <ContactChannel
          testId="contact-whatsapp"
          href={waUrl}
          external
          icon={
            <span className="w-12 h-12 rounded-full bg-[#25D366] text-white flex items-center justify-center shrink-0">
              <WhatsappIcon className="w-[26px] h-[26px]" />
            </span>
          }
          label="Chat with us"
          value="+91 93265 80166"
          valueClass="overline text-[#2A1D15] tabular-nums"
        />
        <ContactChannel
          testId="contact-email"
          href={mailUrl}
          icon={
            <span className="w-12 h-12 rounded-full bg-[#8B4513] text-[#FDFBF7] flex items-center justify-center shrink-0">
              <Mail className="w-6 h-6" strokeWidth={1.6} />
            </span>
          }
          label="Email us"
          value="info@giftingconcierge.in"
          valueClass="overline text-[#2A1D15] normal-case tracking-[0.12em] break-all"
        />
      </div>

      <SocialLinks />
    </div>
  );
}

function NameField({ name, setName }) {
  return (
    <div>
      <label htmlFor="gc-name" className="overline text-[#D19C72] block mb-3">
        Your name
      </label>
      <input
        id="gc-name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid="contact-input-name"
        placeholder="e.g. Aryaman"
        className="w-full bg-[#3a2a1f] border border-[#FDFBF7]/10 focus:border-[#D19C72] outline-none px-5 py-4 rounded-md text-[#FDFBF7] placeholder:text-[#FDFBF7]/35 transition-colors"
      />
    </div>
  );
}

function OccasionField({ occasion, setOccasion }) {
  return (
    <div>
      <label htmlFor="gc-occasion" className="overline text-[#D19C72] block mb-3">
        Occasion
      </label>
      <select
        id="gc-occasion"
        value={occasion}
        onChange={(e) => setOccasion(e.target.value)}
        data-testid="contact-input-occasion"
        className="w-full bg-[#3a2a1f] border border-[#FDFBF7]/10 focus:border-[#D19C72] outline-none px-5 py-4 rounded-md text-[#FDFBF7] transition-colors appearance-none"
        style={{
          backgroundImage:
            "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2218%22 height=%2218%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23D19C72%22 stroke-width=%221.6%22><polyline points=%226 9 12 15 18 9%22></polyline></svg>')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 1rem center",
          paddingRight: "2.75rem",
        }}
      >
        <option value="">Select an occasion</option>
        {occasionOptions.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function DetailsField({ details, setDetails }) {
  return (
    <div>
      <label htmlFor="gc-details" className="overline text-[#D19C72] block mb-3">
        A few details
      </label>
      <textarea
        id="gc-details"
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        rows={4}
        data-testid="contact-input-details"
        placeholder="Who is it for, the feeling you want to convey, any budget or timeline…"
        className="w-full bg-[#3a2a1f] border border-[#FDFBF7]/10 focus:border-[#D19C72] outline-none px-5 py-4 rounded-md text-[#FDFBF7] placeholder:text-[#FDFBF7]/35 transition-colors resize-none"
      />
    </div>
  );
}

function SubmitRow({ mailUrl }) {
  return (
    <>
      <button
        type="submit"
        data-testid="contact-submit-whatsapp"
        className="group w-full inline-flex items-center justify-center gap-3 bg-[#a05a26] hover:bg-[#8B4513] text-[#FDFBF7] pl-3 pr-7 py-3 rounded-full transition-colors duration-300"
      >
        <span className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105">
          <WhatsappIcon className="w-[24px] h-[24px]" />
        </span>
        <span className="overline text-[12px] tracking-[0.22em] leading-none">
          Send via WhatsApp
        </span>
      </button>

      <div className="flex items-center gap-3 text-[#FDFBF7]/40 text-xs">
        <div className="flex-1 h-px bg-[#FDFBF7]/15" />
        <span>or</span>
        <div className="flex-1 h-px bg-[#FDFBF7]/15" />
      </div>

      <a
        href={mailUrl}
        data-testid="contact-submit-email"
        className="block text-center text-sm font-light text-[#D19C72] hover:text-[#FDFBF7] transition-colors underline underline-offset-4 decoration-[#D19C72]/40"
      >
        Send this as an email instead
      </a>
    </>
  );
}

function ContactForm({ name, setName, occasion, setOccasion, details, setDetails, waUrl, mailUrl }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9 }}
      className="lg:col-span-7 bg-[#2A1D15] text-[#FDFBF7] p-8 md:p-12 lg:p-14 rounded-md"
      data-testid="contact-form-panel"
    >
      <h3 className="font-serif-display text-3xl md:text-4xl tracking-tight text-[#FDFBF7]">
        Tell us about your gift
      </h3>
      <p className="mt-2 text-sm md:text-base text-[#FDFBF7]/60 font-light">
        We'll receive your note straight on WhatsApp.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          window.open(waUrl, "_blank", "noopener,noreferrer");
        }}
        className="mt-10 space-y-7"
      >
        <NameField name={name} setName={setName} />
        <OccasionField occasion={occasion} setOccasion={setOccasion} />
        <DetailsField details={details} setDetails={setDetails} />
        <SubmitRow mailUrl={mailUrl} />
      </form>
    </motion.div>
  );
}

export default function Contact() {
  const [name, setName] = useState("");
  const [occasion, setOccasion] = useState("");
  const [details, setDetails] = useState("");

  const message = useMemo(() => {
    const parts = [];
    parts.push("Hi Gifting Concierge!");
    if (name.trim()) parts.push(`I'm ${name.trim()}.`);
    if (occasion) parts.push(`Occasion: ${occasion}.`);
    if (details.trim()) parts.push(`A few details: ${details.trim()}`);
    if (!name && !occasion && !details) {
      parts.push("I'd love to start a gift with you.");
    }
    return parts.join("\n");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, occasion, details]);

  const waUrl = `https://wa.me/919326580166?text=${encodeURIComponent(message)}`;
  const mailUrl = `mailto:info@giftingconcierge.in?subject=${encodeURIComponent(
    "A new gifting brief"
  )}&body=${encodeURIComponent(message)}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#fbf1ea] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-40 -right-32 w-[480px] h-[480px] rounded-full bg-[#d19c72]/15 blur-3xl pointer-events-none"
      />

      <div className="max-w-[1340px] mx-auto px-6 md:px-10 lg:px-16 xl:px-24 grid lg:grid-cols-12 gap-10 md:gap-16 items-start">
        <ContactInfo waUrl={waUrl} mailUrl={mailUrl} />
        <ContactForm
          name={name}
          setName={setName}
          occasion={occasion}
          setOccasion={setOccasion}
          details={details}
          setDetails={setDetails}
          waUrl={waUrl}
          mailUrl={mailUrl}
        />
      </div>
    </section>
  );
}
